import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, Linkedin, Mail, ArrowRight, Award, Loader2, FileText } from 'lucide-react';
import { API } from '../utils/api';
import {
  AUTHORS,
  getAuthorBySlug,
  buildAuthorPersonSchema,
} from '../config/authors';
import NotFoundPage from './NotFoundPage';

const SITE_URL = 'https://sinadicciones.org';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function useAuthorSeo(author, articles) {
  useEffect(() => {
    if (!author) return;
    const url = `${SITE_URL}/autor/${author.slug}`;
    const title = `${author.name} — ${author.role} | SinAdicciones.org`;
    const description = author.bio;
    const imageAbs = author.image ? `${SITE_URL}${author.image}` : '';

    document.title = title;

    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('author', author.name);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'profile', 'property');
    setMeta('og:url', url, 'property');
    if (imageAbs) setMeta('og:image', imageAbs, 'property');
    setMeta('twitter:card', 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (imageAbs) setMeta('twitter:image', imageAbs);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    const personSchema = buildAuthorPersonSchema(author);
    const profileSchema = {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: { '@context': 'https://schema.org', ...personSchema },
      url,
      name: title,
      description,
      inLanguage: 'es-CL',
    };
    const itemList = (articles && articles.length > 0)
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: articles.slice(0, 50).map((a, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${SITE_URL}/blog/${a.slug}`,
            name: a.title,
          })),
        }
      : null;

    const blocks = [
      { id: 'author-profile-jsonld', body: profileSchema },
      ...(itemList ? [{ id: 'author-itemlist-jsonld', body: itemList }] : []),
    ];

    blocks.forEach((b) => {
      const old = document.getElementById(b.id);
      if (old) old.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = b.id;
      s.text = JSON.stringify(b.body);
      document.head.appendChild(s);
    });

    return () => {
      blocks.forEach((b) => {
        const old = document.getElementById(b.id);
        if (old) old.remove();
      });
    };
  }, [author, articles]);
}

export default function AuthorPage() {
  const { slug } = useParams();
  const author = getAuthorBySlug(slug);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!author) return;
    axios
      .get(API + '/articles/public', { params: { limit: 200 } })
      .then((res) => {
        const all = res.data?.articles || res.data || [];
        const authorFirstName = author.name.toLowerCase().split(' ')[0];
        const authorFullLower = author.name.toLowerCase();
        const isMarketing = author.categories?.some((c) => c.toLowerCase() === 'marketing');
        const filtered = all.filter((a) => {
          // Match by author_name (case-insensitive, full or first name)
          const an = (a.author_name || '').toLowerCase();
          if (an && (an.includes(authorFullLower) || an.includes(authorFirstName))) return true;
          // For Marketing-only authors (Nelson), also surface articles in their category
          // even if author_name wasn't updated yet
          if (isMarketing) {
            const cat = (a.category || '').toLowerCase();
            if (cat === 'marketing') return true;
          }
          return false;
        });
        setArticles(filtered);
        setLoading(false);
      })
      .catch(() => {
        setArticles([]);
        setLoading(false);
      });
  }, [author]);

  useAuthorSeo(author, articles);

  if (!author) return <NotFoundPage />;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        {/* Hero del autor */}
        <section className="border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20">
            <Link
              to="/blog"
              className="inline-flex items-center text-slate-400 hover:text-emerald-400 mb-8 transition-colors text-sm"
              data-testid="back-to-blog"
            >
              ← Volver al blog
            </Link>
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              {/* Foto */}
              <div className="flex-shrink-0">
                <img
                  src={author.image}
                  alt={author.name}
                  className="w-40 h-40 md:w-48 md:h-48 rounded-2xl object-cover border-2 border-slate-800 shadow-xl"
                  data-testid="author-image"
                />
              </div>
              {/* Bio */}
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">
                  {author.role}
                </p>
                <h1
                  className="text-3xl md:text-5xl font-bold text-slate-50 leading-tight"
                  data-testid="author-name"
                >
                  {author.name}
                </h1>
                <p className="text-base md:text-lg text-slate-300 mt-4 leading-relaxed max-w-2xl">
                  {author.bio}
                </p>
                {/* Expertise badges */}
                {author.expertise && author.expertise.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {author.expertise.map((e) => (
                      <span
                        key={e}
                        className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs rounded-full"
                      >
                        {e}
                      </span>
                    ))}
                  </div>
                )}
                {/* Social */}
                <div className="flex flex-wrap items-center gap-4 mt-6">
                  {author.linkedin && (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noopener noreferrer me"
                      className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      data-testid="author-linkedin"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                  )}
                  {author.email && (
                    <a
                      href={`mailto:${author.email}`}
                      className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300 transition-colors"
                      data-testid="author-email"
                    >
                      <Mail className="w-4 h-4" />
                      {author.email}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lista de artículos */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Award className="w-5 h-5 text-emerald-400" />
              <h2 className="text-2xl font-bold text-slate-50">
                Artículos publicados
                <span className="text-slate-500 font-normal text-base ml-2">
                  ({articles.length})
                </span>
              </h2>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-16 bg-slate-900 rounded-xl border border-slate-800">
                <FileText className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400" data-testid="no-articles">
                  Este autor aún no tiene artículos publicados.
                </p>
                <Link
                  to="/blog"
                  className="inline-block mt-4 text-emerald-400 hover:underline"
                >
                  Ver todos los artículos →
                </Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="author-articles-list">
                {articles.map((a) => (
                  <Link
                    key={a.article_id}
                    to={`/blog/${a.slug}`}
                    className="block"
                    data-testid={`article-${a.slug}`}
                  >
                    <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-colors group">
                      <CardContent className="p-5">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-2">
                          {a.category && (
                            <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded">
                              {a.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(a.created_at)}
                          </span>
                          {a.reading_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {a.reading_time} min
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold text-slate-100 group-hover:text-emerald-400 transition-colors leading-snug">
                          {a.title}
                        </h3>
                        {a.excerpt && (
                          <p className="text-slate-400 text-sm mt-2 line-clamp-2">
                            {a.excerpt}
                          </p>
                        )}
                        <div className="text-emerald-400 text-sm font-medium mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          Leer artículo <ArrowRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Otros autores */}
        <section className="py-12 border-t border-slate-800 bg-slate-900/30">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <h3 className="text-lg font-semibold text-slate-50 mb-6">
              Otros autores del equipo
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.values(AUTHORS)
                .filter((a) => a.slug !== author.slug)
                .map((other) => (
                  <Link
                    key={other.slug}
                    to={`/autor/${other.slug}`}
                    className="flex items-center gap-4 p-4 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-xl transition-colors group"
                    data-testid={`other-author-${other.slug}`}
                  >
                    <img
                      src={other.image}
                      alt={other.name}
                      className="w-14 h-14 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-100 group-hover:text-emerald-400 transition-colors">
                        {other.name}
                      </p>
                      <p className="text-xs text-slate-500">{other.role}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
